import React, { useState, useEffect } from 'react';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import AlertModal from '../components/AlertModal';
import { ToastContext } from '../App';

interface Student {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  role: string;
  student_category: string;
  is_active: boolean;
  is_approved: boolean;
  has_fixed_monthly_fee: boolean;
  monthly_fee_amount: number;
  created_at: string;
}

interface PendingStudent {
  id: number;
  name: string;
  email: string;
  created_at: string;
}

const StudentsPage: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [pendingStudents, setPendingStudents] = useState<PendingStudent[]>([]);
  const [loadingPendentes, setLoadingPendentes] = useState(false);
  const [loadingAprovados, setLoadingAprovados] = useState(false);
  const [loadingConvidados, setLoadingConvidados] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pendingStudentId, setPendingStudentId] = useState<number | null>(null);
  const [pendingStudentName, setPendingStudentName] = useState('');
  const [filter, setFilter] = useState<'all' | 'student' | 'external'>('all');
  
  // Estados para modais de confirmação
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  const [studentToReject, setStudentToReject] = useState<number | null>(null);
  const [studentToResetPassword, setStudentToResetPassword] = useState<number | null>(null);
  
  // Estados para mensalidade fixa
  const [fixedMonthlyFee, setFixedMonthlyFee] = useState(false);
  const [monthlyFeeAmount, setMonthlyFeeAmount] = useState('');
  
  // 1. Adicionar estado de pesquisa para aprovados e convidados
  const [approvedSearch, setApprovedSearch] = useState('');
  const [guestsSearch, setGuestsSearch] = useState('');
  
  const { updatePendingStudentsCount } = useAuth();
  const { showToast } = React.useContext(ToastContext);

  useEffect(() => {
    loadStudents();
    loadPendingStudents();
  }, []);

  const loadStudents = async () => {
    setLoadingAprovados(true);
    setLoadingConvidados(true);
    setError('');
    
    try {
      const data = await apiCall('/auth/students', { method: 'GET' });
      if (data.students) {
        setStudents(data.students);
      } else {
        setError('Erro ao carregar alunos');
      }
    } catch (err) {
      setError('Erro ao comunicar com o servidor');
    } finally {
      setLoadingAprovados(false);
      setLoadingConvidados(false);
    }
  };

  const loadPendingStudents = async () => {
    setLoadingPendentes(true);
    try {
      const data = await apiCall('/auth/pending-students', { method: 'GET' });
      if (data.pending_students) {
        setPendingStudents(data.pending_students);
        updatePendingStudentsCount(data.pending_students.length);
      }
    } catch (err) {
      console.error('Erro ao carregar alunos pendentes:', err);
    } finally {
      setLoadingPendentes(false);
    }
  };

  const approveStudent = async (studentId: number, category: 'student' | 'external' | 'guest') => {
    try {
      const data = await apiCall('/auth/approve-student', {
        method: 'POST',
        body: JSON.stringify({
          student_id: studentId,
          approved: true,
          student_category: category
        })
      });
      
      if (data.message) {
        await loadPendingStudents();
        await loadStudents();
        setShowApprovalModal(false);
        setPendingStudentId(null);
        setPendingStudentName('');
        showToast('Aluno aprovado com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao aprovar aluno');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const showRejectConfirmation = (studentId: number) => {
    setStudentToReject(studentId);
    setShowRejectModal(true);
  };

  const rejectStudent = async () => {
    if (!studentToReject) return;

    try {
      const data = await apiCall('/auth/approve-student', {
        method: 'POST',
        body: JSON.stringify({
          student_id: studentToReject,
          approved: false
        })
      });
      
      if (data.message) {
        await loadPendingStudents();
        await loadStudents();
        setShowRejectModal(false);
        setStudentToReject(null);
        showToast('Aluno rejeitado com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao rejeitar aluno');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const toggleStudentStatus = async (studentId: number, newStatus: boolean) => {
    try {
      const data = await apiCall(`/auth/student/${studentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: newStatus })
      });
      
      if (data.message) {
        await loadStudents();
        setShowDetailsModal(false);
        setSelectedStudent(null);
        showToast('Status do aluno atualizado com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao atualizar status');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const updateStudentEmail = async (studentId: number, email: string) => {
    try {
      const data = await apiCall(`/auth/student/${studentId}/email`, {
        method: 'PUT',
        body: JSON.stringify({ email })
      });
      
      if (data.message) {
        await loadStudents();
        setShowDetailsModal(false);
        setSelectedStudent(null);
        showToast('Email do aluno atualizado com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao atualizar email');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const updateStudentCategory = async (studentId: number, category: string) => {
    try {
      const data = await apiCall(`/auth/student/${studentId}/category`, {
        method: 'PUT',
        body: JSON.stringify({ student_category: category })
      });
      
      if (data.message) {
        await loadStudents();
        setShowDetailsModal(false);
        setSelectedStudent(null);
        showToast('Categoria do aluno atualizada com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao atualizar categoria');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const updateStudentFixedMonthlyFee = async (studentId: number, hasFixedFee: boolean, amount: number) => {
    try {
      const data = await apiCall(`/auth/student/${studentId}/fixed-monthly-fee`, {
        method: 'PUT',
        body: JSON.stringify({ 
          has_fixed_monthly_fee: hasFixedFee,
          monthly_fee_amount: amount
        })
      });
      
      if (data.message) {
        await loadStudents();
        setShowDetailsModal(false);
        setSelectedStudent(null);
        showToast('Mensalidade fixa atualizada com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao atualizar mensalidade fixa');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const showResetPasswordConfirmation = (studentId: number) => {
    setStudentToResetPassword(studentId);
    setShowResetPasswordModal(true);
  };

  const resetStudentPassword = async () => {
    if (!studentToResetPassword) return;

    try {
      const data = await apiCall(`/auth/student/${studentToResetPassword}/password`, {
        method: 'PUT'
      });
      
      if (data.message) {
        setAlertTitle('Sucesso');
        setAlertMessage('Password do aluno redefinida com sucesso! Nova password enviada por email.');
        setAlertType('success');
        setShowAlertModal(true);
        setShowResetPasswordModal(false);
        setStudentToResetPassword(null);
        showToast('Password do aluno redefinida com sucesso!', 'success');
      } else {
        setError(data.error || 'Erro ao redefinir password');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao comunicar com o servidor');
    }
  };

  const viewStudentDetails = async (studentId: number) => {
    try {
      const data = await apiCall(`/auth/student/${studentId}`, { method: 'GET' });
      if (data.student) {
        setSelectedStudent(data.student);
        // Inicializar estados de mensalidade fixa
        setFixedMonthlyFee(data.student.has_fixed_monthly_fee || false);
        setMonthlyFeeAmount(data.student.monthly_fee_amount ? data.student.monthly_fee_amount.toString() : '');
        setShowDetailsModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar detalhes do aluno');
    }
  };

  const showApprovalModalForStudent = (studentId: number, studentName: string) => {
    setPendingStudentId(studentId);
    setPendingStudentName(studentName);
    setShowApprovalModal(true);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? 'success' : 'secondary';
  };

  const getCategoryBadge = (category: string, role: string) => {
    if (role === 'guest') return 'warning';
    if (category === 'external') return 'info';
    return 'success';
  };

  const getCategoryText = (category: string, role: string) => {
    if (role === 'guest') return 'Convidado';
    if (category === 'external') return 'Externo';
    return 'Aluno';
  };

  const filteredStudents = students.filter(student => {
    if (filter === 'all') return true;
    if (filter === 'student') return student.student_category === 'student' && student.role === 'student';
    if (filter === 'external') return student.student_category === 'external' && student.role === 'student';
    return true;
  });

  const studentsByCategory = {
    student: students.filter(s => s.student_category === 'student' && s.role === 'student'),
    external: students.filter(s => s.student_category === 'external' && s.role === 'student'),
    guest: students.filter(s => s.role === 'guest' || s.student_category === 'guest')
  };

  // 2. Filtrar alunos aprovados e convidados pelo campo de pesquisa
  const filteredApprovedStudents = filteredStudents.filter(student =>
    student.name.toLowerCase().includes(approvedSearch.toLowerCase()) ||
    student.email.toLowerCase().includes(approvedSearch.toLowerCase())
  );
  const filteredApprovedByCategory = {
    student: studentsByCategory.student.filter(student =>
      student.name.toLowerCase().includes(approvedSearch.toLowerCase()) ||
      student.email.toLowerCase().includes(approvedSearch.toLowerCase())
    ),
    external: studentsByCategory.external.filter(student =>
      student.name.toLowerCase().includes(approvedSearch.toLowerCase()) ||
      student.email.toLowerCase().includes(approvedSearch.toLowerCase())
    )
  };
  const filteredGuests = studentsByCategory.guest.filter(student =>
    student.name.toLowerCase().includes(guestsSearch.toLowerCase()) ||
    student.email.toLowerCase().includes(guestsSearch.toLowerCase())
  );

  // 2. Ordenar alunos por nome antes de mapear/renderizar
  const sortedPendingStudents = [...pendingStudents].sort((a, b) => a.name.localeCompare(b.name));
  const sortedApprovedByCategory = {
    student: filteredApprovedByCategory.student.slice().sort((a, b) => a.name.localeCompare(b.name)),
    external: filteredApprovedByCategory.external.slice().sort((a, b) => a.name.localeCompare(b.name))
  };
  const sortedGuests = filteredGuests.slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header bg-warning text-dark">
              <h5><i className="fas fa-users me-2"></i>Gestão de Utilizadores</h5>
            </div>
            <div className="card-body">
              {/* Removido bloco de erro direto, manter apenas Toast */}

              {/* Tabs */}
              <ul className="nav nav-tabs w-100" id="studentsTabs" role="tablist">
                <li className="nav-item flex-fill" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                  >
                    <i className="fas fa-clock me-1"></i>Pedidos Pendentes
                    {pendingStudents.length > 0 && (
                      <span className="badge bg-warning text-dark ms-1">{pendingStudents.length}</span>
                    )}
                  </button>
                </li>
                <li className="nav-item flex-fill" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'approved' ? 'active' : ''}`}
                    onClick={() => setActiveTab('approved')}
                  >
                    <i className="fas fa-check-circle me-1"></i>Alunos Aprovados
                  </button>
                </li>
                <li className="nav-item flex-fill" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'guests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('guests')}
                  >
                    <i className="fas fa-user-clock me-1"></i>Convidados
                  </button>
                </li>
              </ul>

              <div className="tab-content mt-3">
                {/* Tab Pedidos Pendentes */}
                {activeTab === 'pending' && (
                  <div className="tab-pane fade show active">
                    <h6 className="text-warning">
                      <i className="fas fa-clock me-2"></i>Alunos Pendentes de Aprovação
                    </h6>
                    <div id="pendingStudents">
                      {loadingPendentes ? (
                        <div className="d-flex justify-content-center my-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">A carregar...</span>
                          </div>
                        </div>
                      ) : sortedPendingStudents.length === 0 ? (
                        <p className="text-center text-muted">Nenhum aluno pendente de aprovação.</p>
                      ) : (
                        <div className="row">
                          {sortedPendingStudents.map(student => (
                            <div key={student.id} className="col-md-6 col-lg-4 mt-1">
                              <div className="card">
                                <div className="card-body">
                                  <h6 className="card-title pending-student-name">{student.name}</h6>
                                  <p className="text-muted mb-2">{student.email}</p>
                                  <small className="text-muted">
                                    Registado em: {new Date(student.created_at).toLocaleDateString('pt-PT')}
                                  </small>
                                  <div className="mt-3">
                                    <button
                                      className="btn btn-success btn-sm me-2"
                                      onClick={() => showApprovalModalForStudent(student.id, student.name)}
                                    >
                                      <i className="fas fa-check me-1"></i>Aprovar
                                    </button>
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() => showRejectConfirmation(student.id)}
                                    >
                                      <i className="fas fa-times me-1"></i>Rejeitar
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab Alunos Aprovados */}
                {activeTab === 'approved' && (
                  <div className="tab-pane fade show active">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-success mb-0">
                        <i className="fas fa-check-circle me-2"></i>Lista de Alunos Aprovados
                      </h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setFilter('all')}
                        >
                          <i className="fas fa-users me-1"></i>Todos
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${filter === 'student' ? 'btn-success' : 'btn-outline-success'}`}
                          onClick={() => setFilter('student')}
                        >
                          <i className="fas fa-user-check me-1"></i>Ativos
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${filter === 'external' ? 'btn-info' : 'btn-outline-info'}`}
                          onClick={() => setFilter('external')}
                        >
                          <i className="fas fa-user-plus me-1"></i>Externos
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Pesquisar aluno aprovado..."
                      value={approvedSearch}
                      onChange={e => setApprovedSearch(e.target.value)}
                    />
                    <div id="approvedStudents">
                      {loadingAprovados ? (
                        <div className="d-flex justify-content-center my-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">A carregar...</span>
                          </div>
                        </div>
                      ) : filteredApprovedStudents.length === 0 ? (
                        <p className="text-center text-muted">Nenhum aluno aprovado.</p>
                      ) : (
                        <div>
                          {/* Alunos Regulares */}
                          {sortedApprovedByCategory.student.length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-success mb-3">
                                <i className="fas fa-user-graduate me-2"></i>Alunos Regulares ({sortedApprovedByCategory.student.length})
                              </h6>
                              <div className="row">
                                {sortedApprovedByCategory.student.map(student => (
                                  <div key={student.id} className="col-md-6 col-lg-4 mt-1">
                                    <div className="card">
                                      <div className="card-body">
                                        <div className="student-name mb-1">
                                          {student.name}
                                          <span className="badge bg-success ms-2">Aluno</span>
                                          <span className={`badge bg-${getStatusBadge(student.is_active)} ms-2`}>
                                            {student.is_active ? 'Ativo' : 'Inativo'}
                                          </span>
                                        </div>
                                        <div className="student-email">{student.email}</div>
                                        {student.phone && (
                                          <div className="student-email">{student.phone}</div>
                                        )}
                                        <small className="text-muted">
                                          Registado em {new Date(student.created_at).toLocaleDateString('pt-PT')}
                                        </small>
                                        <div className="mt-3 d-flex gap-2">
                                          <button 
                                            className="btn btn-sm btn-outline-primary me-2" 
                                            onClick={() => viewStudentDetails(student.id)}
                                          >
                                            <i className="fas fa-eye"></i> Ver
                                          </button>
                                          <button 
                                            className={`btn btn-sm me-2 ${student.is_active ? 'btn-danger' : 'btn-success'}`}
                                            onClick={() => toggleStudentStatus(student.id, !student.is_active)}
                                          >
                                            <i className={`fas fa-${student.is_active ? 'pause' : 'play'} me-1`}></i>
                                            {student.is_active ? 'Desativar' : 'Ativar'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Alunos Externos */}
                          {sortedApprovedByCategory.external.length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-info mb-3">
                                <i className="fas fa-user-tie me-2"></i>Alunos Externos ({sortedApprovedByCategory.external.length})
                              </h6>
                              <div className="row">
                                {sortedApprovedByCategory.external.map(student => (
                                  <div key={student.id} className="col-md-6 col-lg-4 mb-3">
                                    <div className="card">
                                      <div className="card-body">
                                        <div className="student-name mb-1">
                                          {student.name}
                                          <span className="badge bg-info ms-2">Externo</span>
                                          <span className={`badge bg-${getStatusBadge(student.is_active)} ms-2`}>
                                            {student.is_active ? 'Ativo' : 'Inativo'}
                                          </span>
                                        </div>
                                        <div className="student-email">{student.email}</div>
                                        {student.phone && (
                                          <div className="student-email">{student.phone}</div>
                                        )}
                                        <small className="text-muted">
                                          Registado em {new Date(student.created_at).toLocaleDateString('pt-PT')}
                                        </small>
                                        <div className="mt-3 d-flex gap-2">
                                          <button 
                                            className="btn btn-sm btn-outline-primary me-2" 
                                            onClick={() => viewStudentDetails(student.id)}
                                          >
                                            <i className="fas fa-eye"></i> Ver
                                          </button>
                                          <button 
                                            className={`btn btn-sm me-2 ${student.is_active ? 'btn-danger' : 'btn-success'}`}
                                            onClick={() => toggleStudentStatus(student.id, !student.is_active)}
                                          >
                                            <i className={`fas fa-${student.is_active ? 'pause' : 'play'} me-1`}></i>
                                            {student.is_active ? 'Desativar' : 'Ativar'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab Convidados */}
                {activeTab === 'guests' && (
                  <div className="tab-pane fade show active">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-warning mb-0">
                        <i className="fas fa-user-clock me-2"></i>Lista de Convidados
                      </h6>
                    </div>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Pesquisar convidado..."
                      value={guestsSearch}
                      onChange={e => setGuestsSearch(e.target.value)}
                    />
                    <div id="guestStudents">
                      {loadingConvidados ? (
                        <div className="d-flex justify-content-center my-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">A carregar...</span>
                          </div>
                        </div>
                      ) : sortedGuests.length === 0 ? (
                        <p className="text-center text-muted">Nenhum convidado aprovado.</p>
                      ) : (
                        <div className="mb-4">
                          <h6 className="text-warning mb-3">
                            <i className="fas fa-user-clock me-2"></i>Convidados ({sortedGuests.length})
                          </h6>
                          <div className="row">
                            {sortedGuests.map(student => (
                              <div key={student.id} className="col-md-6 col-lg-4 mt-1">
                                <div className="card">
                                  <div className="card-body">
                                    <div className="student-name mb-1">
                                      {student.name}
                                      <span className="badge bg-warning ms-2">Convidado</span>
                                      <span className={`badge bg-${getStatusBadge(student.is_active)} ms-2`}>
                                        {student.is_active ? 'Ativo' : 'Inativo'}
                                      </span>
                                    </div>
                                    <div className="student-email">{student.email}</div>
                                    {student.phone && (
                                      <div className="student-email">{student.phone}</div>
                                    )}
                                    <small className="text-muted">
                                      Registado em {new Date(student.created_at).toLocaleDateString('pt-PT')}
                                    </small>
                                    <div className="mt-3 d-flex gap-2">
                                      <button 
                                        className="btn btn-sm btn-outline-primary me-2" 
                                        onClick={() => viewStudentDetails(student.id)}
                                      >
                                        <i className="fas fa-eye"></i> Ver
                                      </button>
                                      <button 
                                        className={`btn btn-sm me-2 ${student.is_active ? 'btn-danger' : 'btn-success'}`}
                                        onClick={() => toggleStudentStatus(student.id, !student.is_active)}
                                      >
                                        <i className={`fas fa-${student.is_active ? 'pause' : 'play'} me-1`}></i>
                                        {student.is_active ? 'Desativar' : 'Ativar'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Aprovação */}
      {showApprovalModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Aprovar Aluno</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowApprovalModal(false);
                    setPendingStudentId(null);
                    setPendingStudentName('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Selecione o tipo de aprovação para <strong>{pendingStudentName}</strong>:</p>
                <div className="d-grid gap-2">
                  <button 
                    type="button" 
                    className="btn btn-success btn-lg" 
                    onClick={() => approveStudent(pendingStudentId!, 'student')}
                  >
                    <i className="fas fa-user-graduate me-2"></i>Aprovar como Aluno Regular
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-info btn-lg" 
                    onClick={() => approveStudent(pendingStudentId!, 'external')}
                  >
                    <i className="fas fa-user-tie me-2"></i>Aprovar como Aluno Externo
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-warning btn-lg" 
                    onClick={() => approveStudent(pendingStudentId!, 'guest')}
                  >
                    <i className="fas fa-user-clock me-2"></i>Aprovar como Convidado
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowApprovalModal(false);
                    setPendingStudentId(null);
                    setPendingStudentName('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Aluno */}
      {showDetailsModal && selectedStudent && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Gestão do {selectedStudent.role === 'guest' ? 'Convidado' : 'Aluno'}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedStudent(null);
                    setFixedMonthlyFee(false);
                    setMonthlyFeeAmount('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Seção 1: Informações Básicas */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-user me-2"></i>
                      Informações Básicas
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">Nome</small>
                          <strong>{selectedStudent.name}</strong>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">Email</small>
                          <strong>{selectedStudent.email}</strong>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">Telefone</small>
                          <strong>{selectedStudent.phone || <span className="text-muted fw-normal">Não indicado</span>}</strong>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">Categoria</small>
                          <span className="badge bg-secondary">{getCategoryText(selectedStudent.student_category, selectedStudent.role)}</span>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">Status</small>
                          <span className={`badge ${selectedStudent.is_active ? 'bg-success' : 'bg-danger'}`}>
                            {selectedStudent.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">Registado em</small>
                          <strong>{new Date(selectedStudent.created_at).toLocaleDateString('pt-PT')}</strong>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex flex-column">
                          <small className="text-muted">ID</small>
                          <strong>{selectedStudent.id}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Configurações (apenas para alunos) */}
                {selectedStudent.role !== 'guest' && (
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="fas fa-cog me-2"></i>
                        Configurações
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Categoria */}
                      <div className="mb-3">
                        <label htmlFor="studentCategory" className="form-label fw-bold">Categoria do Aluno</label>
                        <div className="d-flex gap-2">
                          <select 
                            className="form-select" 
                            id="studentCategory"
                            defaultValue={selectedStudent.student_category}
                          >
                            <option value="student">Regular</option>
                            <option value="external">Externo</option>
                          </select>
                          <button 
                            className="btn btn-outline-info" 
                            onClick={() => {
                              const select = document.getElementById('studentCategory') as HTMLSelectElement;
                              updateStudentCategory(selectedStudent.id, select.value);
                            }}
                          >
                            <i className="fas fa-save me-1"></i>
                            Atualizar
                          </button>
                        </div>
                      </div>

                      {/* Mensalidade Fixa */}
                      <div className="mb-3">
                        <label className="form-label fw-bold">Mensalidade Fixa</label>
                        <div className="form-check mb-2">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="fixedMonthlyFee"
                            checked={fixedMonthlyFee}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setFixedMonthlyFee(newValue);
                              if (!newValue) {
                                setMonthlyFeeAmount('');
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor="fixedMonthlyFee">
                            Ativar mensalidade fixa
                          </label>
                        </div>
                        {fixedMonthlyFee && (
                          <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                              <input 
                                type="number" 
                                className="form-control" 
                                id="monthlyFeeAmount" 
                                placeholder="Valor mensal (€)"
                                step="0.01"
                                min="0"
                                value={monthlyFeeAmount}
                                onChange={(e) => setMonthlyFeeAmount(e.target.value)}
                              />
                            </div>
                            <button 
                              className="btn btn-outline-warning" 
                              onClick={() => {
                                const amount = fixedMonthlyFee ? parseFloat(monthlyFeeAmount) || 0 : 0;
                                updateStudentFixedMonthlyFee(selectedStudent.id, fixedMonthlyFee, amount);
                              }}
                            >
                              <i className="fas fa-save me-1"></i>
                              Guardar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Seção 3: Gestão de Acesso */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-lock me-2"></i>
                      Gestão de Acesso
                    </h6>
                  </div>
                  <div className="card-body">
                    {/* Email de Acesso */}
                    <div className="mb-3">
                      <label htmlFor="studentEmail" className="form-label fw-bold">Email de Acesso</label>
                      <div className="d-flex gap-2">
                        <input 
                          type="email" 
                          className="form-control" 
                          id="studentEmail" 
                          defaultValue={selectedStudent.email}
                        />
                        <button 
                          type="button" 
                          className="btn btn-primary" 
                          onClick={() => {
                            const input = document.getElementById('studentEmail') as HTMLInputElement;
                            updateStudentEmail(selectedStudent.id, input.value);
                          }}
                        >
                          <i className="fas fa-save me-1"></i>
                          Atualizar
                        </button>
                      </div>
                    </div>

                    {/* Redefinir Password */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Password</label>
                      <button 
                        type="button" 
                        className="btn btn-warning w-100" 
                        onClick={() => showResetPasswordConfirmation(selectedStudent.id)}
                      >
                        <i className="fas fa-key me-1"></i>
                        Redefinir Password
                      </button>
                    </div>
                  </div>
                </div>

                {/* Seção 4: Status da Conta */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-toggle-on me-2"></i>
                      Status da Conta
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Estado atual:</strong>
                        <span className={`badge ms-2 ${selectedStudent.is_active ? 'bg-success' : 'bg-danger'}`}>
                          {selectedStudent.is_active ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>
                      <button 
                        type="button" 
                        className={`btn btn-${selectedStudent.is_active ? 'danger' : 'success'}`} 
                        onClick={() => toggleStudentStatus(selectedStudent.id, !selectedStudent.is_active)}
                      >
                        <i className={`fas fa-${selectedStudent.is_active ? 'pause' : 'play'} me-1`}></i>
                        {selectedStudent.is_active ? 'Desativar Conta' : 'Ativar Conta'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedStudent(null);
                    setFixedMonthlyFee(false);
                    setMonthlyFeeAmount('');
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação para Rejeitar Aluno */}
      <ConfirmationModal
        isOpen={showRejectModal}
        title="Rejeitar Aluno"
        message="Tem certeza que deseja rejeitar este aluno?"
        confirmText="Rejeitar"
        cancelText="Cancelar"
        confirmButtonClass="btn-danger"
        onConfirm={rejectStudent}
        onCancel={() => {
          setShowRejectModal(false);
          setStudentToReject(null);
        }}
      />

      {/* Modal de Confirmação para Redefinir Password */}
      <ConfirmationModal
        isOpen={showResetPasswordModal}
        title="Redefinir Password"
        message="Tem certeza que deseja redefinir a password deste aluno? Uma nova password será gerada e enviada por email."
        confirmText="Redefinir"
        cancelText="Cancelar"
        confirmButtonClass="btn-warning"
        onConfirm={resetStudentPassword}
        onCancel={() => {
          setShowResetPasswordModal(false);
          setStudentToResetPassword(null);
        }}
      />

      {/* Modal de Alerta */}
      <AlertModal
        isOpen={showAlertModal}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        onClose={() => setShowAlertModal(false)}
      />
    </div>
  );
};

export default StudentsPage; 